/**
 * Onglets accessibles (motif ARIA « tabs », activation automatique) : fleches
 * gauche et droite pour passer d'un onglet a l'autre, Debut et Fin pour les
 * extremites. Composant controle : l'onglet actif vit chez l'appelant.
 */
import { useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface DefinitionOnglet<T extends string = string> {
  id: T;
  libelle: string;
  icone?: LucideIcon;
  /** Compteur affiche apres le libelle (nombre de relances...). */
  badge?: number | string;
  desactive?: boolean;
}

export function idOnglet(base: string, onglet: string): string {
  return `${base}-onglet-${onglet}`;
}

export function idPanneauOnglet(base: string, onglet: string): string {
  return `${base}-panneau-${onglet}`;
}

type Deplacement = 'suivant' | 'precedent' | 'premier' | 'dernier';

const TOUCHES: Readonly<Record<string, Deplacement>> = {
  ArrowRight: 'suivant',
  ArrowLeft: 'precedent',
  Home: 'premier',
  End: 'dernier',
};

export function Onglets<T extends string>({
  id,
  libelle,
  onglets,
  actif,
  onChange,
  className = '',
}: {
  /** Prefixe des identifiants, partage avec les PanneauOnglet correspondants. */
  id: string;
  /** Nom accessible de la liste d'onglets. */
  libelle: string;
  onglets: readonly DefinitionOnglet<T>[];
  actif: T;
  onChange: (onglet: T) => void;
  className?: string;
}) {
  const refs = useRef(new Map<T, HTMLButtonElement>());

  const deplacer = (depuis: T, deplacement: Deplacement): void => {
    const activables = onglets.filter((onglet) => !onglet.desactive);
    const position = activables.findIndex((onglet) => onglet.id === depuis);
    const pas = deplacement === 'suivant' ? 1 : -1;
    const cible =
      deplacement === 'premier'
        ? activables[0]
        : deplacement === 'dernier'
          ? activables[activables.length - 1]
          : activables[(position + pas + activables.length) % activables.length];
    if (!cible) return;
    onChange(cible.id);
    refs.current.get(cible.id)?.focus();
  };

  const surTouche = (evenement: KeyboardEvent<HTMLButtonElement>, onglet: T): void => {
    const deplacement = TOUCHES[evenement.key];
    if (!deplacement) return;
    evenement.preventDefault();
    deplacer(onglet, deplacement);
  };

  // Liste defilante (overflow-x-auto), donc rognee aussi en hauteur : le trait
  // de base est une ombre interne, pas une bordure que les onglets
  // chevaucheraient (-mb-px, barre de defilement sous Windows), et l'anneau de
  // focus des onglets est trace vers l'interieur pour ne pas etre coupe.
  return (
    <div
      role="tablist"
      aria-label={libelle}
      className={`flex gap-1 overflow-x-auto shadow-[inset_0_-1px_0_var(--color-bordure)] ${className}`}
    >
      {onglets.map((onglet) => {
        const selectionne = onglet.id === actif;
        const Icone = onglet.icone;
        return (
          <button
            key={onglet.id}
            ref={(element) => {
              if (element) refs.current.set(onglet.id, element);
              else refs.current.delete(onglet.id);
            }}
            type="button"
            role="tab"
            id={idOnglet(id, onglet.id)}
            aria-selected={selectionne}
            aria-controls={selectionne ? idPanneauOnglet(id, onglet.id) : undefined}
            tabIndex={selectionne ? 0 : -1}
            disabled={onglet.desactive}
            onClick={() => onChange(onglet.id)}
            onKeyDown={(evenement) => surTouche(evenement, onglet.id)}
            className={`inline-flex h-10 shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-medium whitespace-nowrap transition-colors focus-visible:-outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
              selectionne
                ? 'border-accent-600 text-accent-700'
                : 'border-transparent text-encre-discrete hover:border-bordure-forte hover:text-encre'
            }`}
          >
            {Icone && <Icone aria-hidden="true" />}
            {onglet.libelle}
            {onglet.badge !== undefined && (
              <span
                className={`rounded-full px-1.5 text-xs leading-5 font-medium ${
                  selectionne ? 'bg-accent-100 text-accent-800' : 'bg-surface-appuyee text-encre-douce'
                }`}
              >
                {onglet.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function PanneauOnglet({
  id,
  onglet,
  actif,
  children,
  className = '',
  garderMonte = false,
}: {
  id: string;
  onglet: string;
  actif: string;
  children: ReactNode;
  className?: string;
  /**
   * Une fois visite, le panneau reste monte (masque) quand on change d'onglet :
   * son etat local (saisie en cours, defilement) survit. Il n'est monte qu'a
   * sa premiere activation, rien n'est charge d'avance.
   */
  garderMonte?: boolean;
}) {
  const estActif = onglet === actif;
  const [visite, setVisite] = useState(estActif);
  // Memoriser la premiere visite pendant le rendu evite un rendu vide suivi
  // d'un second rendu, le temps qu'un effet le fasse.
  if (estActif && !visite) setVisite(true);

  if (!estActif && !(garderMonte && visite)) return null;
  return (
    <div
      role="tabpanel"
      id={idPanneauOnglet(id, onglet)}
      aria-labelledby={idOnglet(id, onglet)}
      tabIndex={0}
      hidden={!estActif}
      className={className}
    >
      {children}
    </div>
  );
}
